import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, resolveImageSrc } from "@/lib/api";
import VendorQRCard from "@/components/VendorQRCard";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import ImageUploader from "@/components/ImageUploader";
import LogoStudioCard from "@/components/LogoStudioCard";
import AiFreeToggle from "@/components/AiFreeToggle";
import { toast } from "sonner";
import { X, Plus } from "lucide-react";
import {
  useVendorCategories,
  VENDOR_CATEGORIES,
} from "@/lib/vendorCategories";

// Legacy profiles may hold free-form category strings ("Ceramics", "art").
// Coerce them to the closest fixed enum value at load time so the picker
// pre-fills correctly and a save doesn't silently drop them.
function _migrateLegacyCategories(cats) {
  if (!cats) return [];
  const valid = new Set(VENDOR_CATEGORIES.map((c) => c.value));
  const out = [];
  for (const raw of cats) {
    const v = String(raw || "").trim().toLowerCase();
    if (valid.has(v)) {
      out.push(v);
    }
  }
  return out.slice(0, 3);
}

export default function VendorProfile() {
  const { user, refresh } = useAuth();
  const catConfig = useVendorCategories();
  const [form, setForm] = useState({ business_name: "", bio: "", website: "", location: "", social_url: "" });
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [portfolio, setPortfolio] = useState([]);
  const [newImg, setNewImg] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        business_name: user.business_name || "",
        bio: user.bio || "",
        website: user.website || "",
        location: user.location || "",
        social_url: user.social_url || "",
      });
      setCategories(_migrateLegacyCategories(user.categories));
      setPortfolio(user.portfolio_images || []);
      setLogoUrl(user.logo_url || "");
    }
  }, [user]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/vendors/me", {
        ...form, categories, portfolio_images: portfolio, logo_url: logoUrl,
      });
      await refresh();
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-14">
      <div className="overline" style={{ color: "var(--terracotta)" }}>Vendor profile</div>
      <h1 className="font-serif text-5xl mt-2">Your story.</h1>
      <p className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}>Organizers see this when reviewing your applications.</p>

      {user?.slug && (
        <div className="mt-6 card-warm rounded-sm p-4 flex flex-wrap items-center gap-3" data-testid="profile-public-url-card">
          <div className="overline" style={{ color: "var(--moss)" }}>Your public page</div>
          <a href={`/vendors/${user.slug}`} target="_blank" rel="noreferrer" className="text-sm underline break-all" data-testid="profile-public-link">
            {window.location.origin}/vendors/{user.slug}
          </a>
          <Button type="button" size="sm" data-testid="profile-copy-link" className="btn-outline rounded-full ml-auto"
                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/vendors/${user.slug}`); toast.success("Link copied — share it anywhere"); }}>
            Copy link
          </Button>
        </div>
      )}

      <div className="mt-6">
        <VendorQRCard slug={user?.slug} businessName={form.business_name || user?.name} />
      </div>

      <div className="mt-6">
        <AiFreeToggle initialValue={user?.ai_free} />
      </div>

      <form onSubmit={save} className="mt-10 space-y-6">
        <Field label="Brand / studio name"><Input data-testid="profile-business" value={form.business_name} onChange={upd("business_name")} /></Field>
        <Field label="Bio"><Textarea data-testid="profile-bio" rows={5} value={form.bio} onChange={upd("bio")} placeholder="Tell your story — how you make, what you make…" /></Field>
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Website"><Input data-testid="profile-website" value={form.website} onChange={upd("website")} placeholder="https://…" /></Field>
          <Field label="Location"><Input data-testid="profile-location" value={form.location} onChange={upd("location")} placeholder="Portland, OR" /></Field>
        </div>
        <Field label="Social media (Instagram, TikTok, Facebook…)">
          <Input data-testid="profile-social" value={form.social_url} onChange={upd("social_url")} placeholder="https://instagram.com/your-studio" />
        </Field>

        <div>
          <Label className="text-xs uppercase tracking-widest">
            Categories{" "}
            <span
              className="lowercase font-normal"
              style={{ color: "var(--ink-soft)" }}
            >
              (pick {catConfig.min || 2}-{catConfig.max || 3} — organizers search on these)
            </span>
          </Label>
          <div
            data-testid="profile-categories"
            className="flex flex-wrap gap-2 mt-2"
          >
            {(catConfig.categories || []).map((c) => {
              const active = categories.includes(c.value);
              const full = categories.length >= (catConfig.max || 3);
              return (
                <button
                  key={c.value}
                  type="button"
                  data-testid={`profile-category-${c.value}`}
                  aria-pressed={active}
                  onClick={() => {
                    if (active) {
                      setCategories(categories.filter((v) => v !== c.value));
                    } else if (!full) {
                      setCategories([...categories, c.value]);
                    }
                  }}
                  disabled={!active && full}
                  className="px-3 py-1.5 rounded-full border-2 text-sm transition"
                  style={{
                    borderColor: active ? "var(--terracotta)" : "var(--border-warm)",
                    backgroundColor: active ? "rgba(201,67,42,0.10)" : "transparent",
                    color: active ? "var(--terracotta)" : "var(--ink)",
                    opacity: !active && full ? 0.45 : 1,
                    cursor: !active && full ? "not-allowed" : "pointer",
                  }}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <div
            className="mt-1.5 text-[11px]"
            style={{ color: "var(--ink-soft)" }}
          >
            {categories.length} of {catConfig.max || 3} selected
          </div>
        </div>

        <LogoStudioCard
          currentLogoUrl={logoUrl}
          onSelect={setLogoUrl}
          businessName={form.business_name}
        />

        <div>
          <Label className="text-xs uppercase tracking-widest">Portfolio images</Label>
          <div className="grid grid-cols-3 gap-3 mt-2">
            {portfolio.map((img, i) => (
              <PortfolioTile
                key={i}
                src={resolveImageSrc(img)}
                onRemove={() => setPortfolio(portfolio.filter((_, idx) => idx !== i))}
              />
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <ImageUploader label="Upload portfolio image" testId="profile-portfolio" onUploaded={(url) => setPortfolio([...portfolio, url])} />
            <span className="text-xs" style={{ color: "var(--ink-soft)" }}>or paste an image URL:</span>
            <Input data-testid="profile-img-input" value={newImg} onChange={(e) => setNewImg(e.target.value)} placeholder="https://image-url.jpg" className="flex-1 min-w-[200px]" />
            <Button type="button" data-testid="profile-img-add" onClick={() => { if (newImg.trim()) { setPortfolio([...portfolio, newImg.trim()]); setNewImg(""); } }} className="btn-outline rounded-full">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <Button type="submit" data-testid="profile-save" disabled={busy} className="btn-primary w-full py-6 rounded-full">
          {busy ? "Saving…" : "Save profile"}
        </Button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

/**
 * PortfolioTile — one portfolio image slot with graceful fallback.
 * Uses local state to swap the <img> for a soft placeholder when the
 * source URL fails (typically a legacy URL from before the domain rename).
 */
function PortfolioTile({ src, onRemove }) {
  const [broken, setBroken] = useState(false);
  return (
    <div
      className="relative aspect-square rounded-sm overflow-hidden"
      style={{ backgroundColor: "var(--sand)" }}
    >
      {broken || !src ? (
        <div className="w-full h-full flex flex-col items-center justify-center text-center px-2"
             style={{ color: "var(--ink-soft)" }}>
          <span className="text-[10px] uppercase tracking-widest">Image unavailable</span>
          <span className="text-[9px] mt-1 opacity-70">Delete + re-upload to fix</span>
        </div>
      ) : (
        <img
          src={src}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setBroken(true)}
        />
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 bg-white/90 rounded-full p-1"
        aria-label="Remove image"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
